import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sparkles, Clock, ChefHat } from 'lucide-react';
import RecipeCleanupPreview from '@/components/Assistant/RecipeCleanupPreview';
import { handleRecipeCleanupRequest, handleRecipeCopyRequest } from '@/services/recipeService';
import { ArrowLeft, Edit, Trash2, Star, Eye, EyeOff, X, ImagePlus, Copy, Activity, Clipboard, Check as CheckIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { writePendingRecipe } from '@/lib/pendingRecipe';
import { useRecipeStore } from '@/stores/recipeStore';
import { useConversationStore } from '@/stores/conversationStore';
import { ImageGenerationModalService } from '@/services/imageModalService';
import { Recipe, RecipeFormData, RecipeSource } from '@/types/recipe';
import { RECIPE_SOURCE_CONFIG, DEFAULT_SOURCE_CONFIG } from '@/config/recipeSourceConfig';
import { chefPersonas } from '@/config/chefPersonas';
import ReactMarkdown from 'react-markdown';
import RecipeFormModern from '@/components/RecipeFormModern';
import { setCurrentRecipe } from '@/services/recipeContextService';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { formatErrorForUser } from '@/utils/errorUtils';
import RecipeNutritionalModal from '@/components/RecipeNutritionalModal';

const RecipeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { restoreScrollPosition } = useScrollPosition();
  const { role } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Edit mode detection
  const isEditMode = searchParams.get('edit') === 'true';

  const {
    recipes,
    // getRecipeById, // No longer directly used in this component
    toggleFavorite,
    toggleShare,
    deleteRecipe,
    updateRecipe,
    // fetchRecipes, // We'll use fetchRecipeById for specific loading
    fetchRecipeById, // Use the new specific fetch action
    loading: recipesLoading
  } = useRecipeStore();

  const {
    isLoading: isAssistantLoading
  } = useConversationStore();

  const [isNutritionalAnalysisModalOpen, setIsNutritionalAnalysisModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const [showCleanupPreview, setShowCleanupPreview] = useState(false);
  const [cleanedMarkdown, setCleanedMarkdown] = useState('');
  const [originalMarkdownForCleanup, setOriginalMarkdownForCleanup] = useState('');
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<RecipeFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadRecipeAsync = async () => {
      if (!id) {
        setIsLoading(false);
        setRecipe(null);
        return;
      }

      setIsLoading(true); // Start loading

      // Try to find in existing store recipes
      const recipeFromStore = recipes.find((r: Recipe) => String(r.id) === String(id));

      if (recipeFromStore) {
        setRecipe(recipeFromStore);
        setCurrentRecipe(recipeFromStore); // Set recipe context for assistant
        setIsLoading(false);
      } else {
        // If not found in local store state, and no global recipe list fetch is in progress,
        // attempt to fetch this specific recipe by its ID.
        if (!recipesLoading) {
          const fetchedRecipe = await fetchRecipeById(id);

          if (fetchedRecipe) {
            setRecipe(fetchedRecipe);
            setCurrentRecipe(fetchedRecipe); // Set recipe context for assistant
            setIsLoading(false);
          } else {
            setRecipe(null);
            setIsLoading(false);
          }
        }
      }
    };

    loadRecipeAsync();

    // Cleanup function
    return () => {
      setCurrentRecipe(null);
    };
  }, [id, recipes, recipesLoading, fetchRecipeById]);

  // Effect for dynamic meta tags and structured data
  useEffect(() => {
    if (recipe) {
      // Update page title
      document.title = `${recipe.title} - Chef Lab`;

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', recipe.description?.substring(0, 160) || '');

      // Add JSON-LD structured data
      let scriptTag = document.getElementById('recipe-schema');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('id', 'recipe-schema');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Recipe",
        "name": recipe.title,
        "description": recipe.description,
        "image": recipe.image_url || '/recipe-placeholder.svg',
        "recipeIngredient": recipe.ingredients ? recipe.ingredients.split('\n').filter(line => line.trim() !== '') : [],
        "recipeInstructions": recipe.method,
        "author": {
          "@type": "Person",
          "name": recipe.creatorName || "Chef Lab"
        },
        "datePublished": recipe.dateCreated ? new Date(recipe.dateCreated).toISOString() : new Date().toISOString()
      });
    }

    // Clean up meta tags and script when component unmounts or recipe is null
    return () => {
      document.title = "Chef Lab: AI-Powered Recipe Creation & Nutrition Assistant"; // Revert to new default title
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', "Chef Lab: Your AI-powered culinary assistant. Create, manage, and discover recipes with intelligent tools, real-time nutrition analysis, and AI-generated food photography."); // Revert to new default description
      }
      const scriptTag = document.getElementById('recipe-schema');
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [recipe]);
  // Removed fetchRecipes from deps, added fetchRecipeById

  // Convert recipe to form data when entering edit mode
  useEffect(() => {
    if (isEditMode && recipe) {
      setFormData({
        title: recipe.title,
        description: recipe.description,
        category: recipe.category || 'Asian',
        tags: recipe.tags,
        ingredients: recipe.ingredients,
        method: recipe.method,
        notes: recipe.notes || '',
        image_url: recipe.image_url,
        has_custom_image: recipe.has_custom_image || false,
        isFavorite: recipe.isFavorite,
        share: recipe.share !== undefined ? recipe.share : true,
      });
    } else if (!isEditMode) {
      setFormData(null);
    }
  }, [isEditMode, recipe]);

  // Edit mode handlers
  const handleSave = async (updatedFormData: RecipeFormData) => {
    if (!recipe) return;
    try {
      setIsSubmitting(true);
      await updateRecipe(recipe.id, updatedFormData);
      if (location.state && location.state.fromManage) {
        const tab = location.state.tab || 'recipes';
        navigate(`/manage?tab=${tab}`);
        return;
      }
      // Remove edit parameter to exit edit mode
      setSearchParams({});
      // Update local recipe state
      const updatedRecipe = { ...recipe, ...updatedFormData };
      setRecipe(updatedRecipe);
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (location.state && location.state.fromManage) {
      const tab = location.state.tab || 'recipes';
      navigate(`/manage?tab=${tab}`);
    } else {
      setSearchParams({}); // Remove edit parameter
      setFormData(null);
    }
  };

  const handleEditClick = () => {
    setSearchParams({ edit: 'true' });
  };

  const handleFavoriteToggle = async () => {
    if (!recipe) return;

    try {
      await toggleFavorite(recipe.id);
    } catch {
    }
  };

  const handleShareToggle = async () => {
    if (!recipe) return;

    try {
      // Pass the current value of `share` to the store action
      await toggleShare(recipe.id, recipe.share);
    } catch {
      // Optional: Add user feedback here, e.e.g., a toast notification
    }
  };

  const handleCloneRecipe = async () => {
    if (!recipe) return;

    try {
      // Trigger the same workflow as "copy this recipe" in assistant
      const result = await handleRecipeCopyRequest(recipe, "copy this recipe");

      if (result.success && result.data) {
        // Store copy data and navigate DIRECTLY to form (not the launcher)
        writePendingRecipe(result.data.formData);
        navigate('/recipe/new/form?from=clone');
      }
    } catch {
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recipe) return;
    try {
      await deleteRecipe(recipe.id);
      setIsDeleteDialogOpen(false);
      navigate('/', { state: { fromRecipeDetail: true } }); // Navigate back to home after deletion
    } catch {
    }
  };

  const handleCopyRecipe = async () => {
    if (!recipe) return;
    try {
      const md = `# ${recipe.title}\n\n${recipe.description || ''}\n\n## Ingredients\n${recipe.ingredients}\n\n## Method\n${recipe.method}${recipe.notes ? `\n\n## Notes\n${recipe.notes}` : ''}`;
      await navigator.clipboard.writeText(md);
      setCopyFeedback('Recipe copied to clipboard!');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch {
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  };

  const assembleMarkdownFromRecipe = (currentRecipe: Recipe): string => {
    return `# ${currentRecipe.title}\n\n${currentRecipe.description}\n\n## Ingredients\n${currentRecipe.ingredients}\n\n## Method\n${currentRecipe.method}`;
  };

  const parseCleanedMarkdownToRecipeFields = (markdown: string): Partial<RecipeFormData> => {
    const newFields: Partial<RecipeFormData> = {};
    try {
      const titleMatch = markdown.match(/^#\s*(.*)/m);
      if (titleMatch) newFields.title = titleMatch[1].trim();

      const ingredientsHeaderMatch = markdown.match(/^##\s*Ingredients/im);
      const methodHeaderMatch = markdown.match(/^##\s*Method/im);

      if (ingredientsHeaderMatch && methodHeaderMatch && ingredientsHeaderMatch.index! < methodHeaderMatch.index!) {
        const descriptionEndIndex = ingredientsHeaderMatch.index!;
        const descriptionText = markdown.substring(titleMatch ? titleMatch[0].length : 0, descriptionEndIndex).trim();
        const cleanedDescription = descriptionText.split('\n').filter(line => line.trim() !== '').join('\n');
        if (cleanedDescription) newFields.description = cleanedDescription;

        const ingredientsText = markdown.substring(ingredientsHeaderMatch.index! + ingredientsHeaderMatch[0].length, methodHeaderMatch.index!).trim();
        newFields.ingredients = ingredientsText;

        const methodText = markdown.substring(methodHeaderMatch.index! + methodHeaderMatch[0].length).trim();
        newFields.method = methodText;

      } else if (ingredientsHeaderMatch) { // Only ingredients, no method
        const descriptionEndIndex = ingredientsHeaderMatch.index!;
        const descriptionText = markdown.substring(titleMatch ? titleMatch[0].length : 0, descriptionEndIndex).trim();
        const cleanedDescription = descriptionText.split('\n').filter(line => line.trim() !== '').join('\n');
        if (cleanedDescription) newFields.description = cleanedDescription;

        const ingredientsText = markdown.substring(ingredientsHeaderMatch.index! + ingredientsHeaderMatch[0].length).trim();
        newFields.ingredients = ingredientsText;
        newFields.method = ''; // Explicitly clear method if not found after ingredients
      } else if (methodHeaderMatch) { // Only method, no ingredients
        const descriptionEndIndex = methodHeaderMatch.index!;
        const descriptionText = markdown.substring(titleMatch ? titleMatch[0].length : 0, descriptionEndIndex).trim();
        const cleanedDescription = descriptionText.split('\n').filter(line => line.trim() !== '').join('\n');
        if (cleanedDescription) newFields.description = cleanedDescription;

        newFields.ingredients = ''; // Explicitly clear ingredients if not found before method
        const methodText = markdown.substring(methodHeaderMatch.index! + methodHeaderMatch[0].length).trim();
        newFields.method = methodText;
      } else { // No ingredients or method, just title and description
        const descriptionText = markdown.substring(titleMatch ? titleMatch[0].length : 0).trim();
        const cleanedDescription = descriptionText.split('\n').filter(line => line.trim() !== '').join('\n');
        if (cleanedDescription) newFields.description = cleanedDescription;
        newFields.ingredients = '';
        newFields.method = '';
      }

      newFields.title = newFields.title ?? recipe?.title ?? '';
      newFields.description = newFields.description ?? recipe?.description ?? '';
      newFields.ingredients = newFields.ingredients ?? recipe?.ingredients ?? '';
      newFields.method = newFields.method ?? recipe?.method ?? '';

    } catch {
      setCleanupError("Failed to parse the cleaned recipe. Please check the format.");
      return recipe ? {
        title: recipe.title,
        description: recipe.description,
        ingredients: recipe.ingredients,
        method: recipe.method,
      } : {};
    }
    return newFields;
  };

  const handleOpenCleanupPreview = async () => {
    if (!recipe) return;
    setIsCleaningUp(true);
    setCleanupError(null);
    const currentMarkdown = assembleMarkdownFromRecipe(recipe);
    setOriginalMarkdownForCleanup(currentMarkdown);

    const result = await handleRecipeCleanupRequest(currentMarkdown);
    if (result.success && result.data) {
      setCleanedMarkdown(result.data as string);
    } else {
      setCleanedMarkdown('');
      setCleanupError(formatErrorForUser(result.error || 'Failed to clean up recipe.'));
    }
    setShowCleanupPreview(true);
    setIsCleaningUp(false);
  };

  const handleAcceptCleanup = () => {
    if (!recipe || !cleanedMarkdown) return;
    const updatedFields = parseCleanedMarkdownToRecipeFields(cleanedMarkdown);

    setFormData(prevData => {
      if (!prevData) return null;
      return {
        ...prevData,
        ...updatedFields,
      };
    });

    setShowCleanupPreview(false);
    setCleanedMarkdown('');
    setOriginalMarkdownForCleanup('');
    setCleanupError(null);
  };

  const handleRejectCleanup = () => {
    setShowCleanupPreview(false);
    setCleanedMarkdown('');
    setOriginalMarkdownForCleanup('');
    setCleanupError(null);
  };

  useEffect(() => {
    // Always scroll to top when opening a recipe
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  // Loading state
  if (isLoading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Recipe not found
  if (!recipe) {
    return (
      <div className="py-8">
        <div className="text-center py-12">
          <div className="space-y-4">
            <div className="text-6xl opacity-20">🔍</div>
            <h1 className="text-2xl font-bold text-slate-900">Recipe Not Found</h1>
            <p className="text-slate-600">
              The recipe you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate('/')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 sm:py-8">
      {/* Toast Notification */}
      {copyFeedback && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] px-4 py-2 bg-slate-900 text-white rounded-full shadow-2xl flex items-center gap-2 text-sm font-bold animate-in fade-in slide-in-from-top-4 duration-300">
          <CheckIcon className="w-4 h-4 text-emerald-400" />
          {copyFeedback}
        </div>
      )}

      {/* Header with navigation */}
      <div className="mb-2">
        <Button
          variant="ghost"
          onClick={() => {
            navigate('/');
            setTimeout(() => restoreScrollPosition(), 100);
          }}
          className="mb-1"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Recipe Content - Conditional rendering for view/edit mode */}
      {isEditMode && formData ? (
        /* Edit Mode - Show Recipe Form with proper container styling */
        <div className="py-2">
          {/* Edit Mode Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
              <div className="w-full sm:w-auto min-w-0">
                <h1 className="text-base sm:text-3xl font-bold text-slate-900 w-full sm:w-auto leading-tight line-clamp-2 mb-1 flex items-center gap-2">
                  <Edit className="h-6 sm:h-8 w-6 sm:w-8 text-blue-600" />
                  Edit Recipe
                </h1>
                <p className="text-slate-600 w-full block leading-tight line-clamp-2 mb-1">
                  Make changes to "{recipe.title}"
                </p>
              </div>
              {/* Icon group: horizontal scroll on mobile, wrap on desktop */}
              <div className="order-2 sm:order-none w-full sm:w-auto flex flex-nowrap sm:flex-wrap gap-1 sm:gap-2 overflow-x-auto sm:overflow-visible no-scrollbar px-1 sm:px-0 mt-1 sm:mt-0">
                {/* Settings & AI Tools Group */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-11 w-11 rounded-full ${recipe.share
                    ? 'bg-blue-500/90 text-white hover:bg-blue-600/90'
                    : 'bg-red-500/90 text-white hover:bg-red-600/90'
                    }`}
                  title={recipe.share ? 'Shared with readers' : 'Private recipe'}
                  onClick={handleShareToggle}
                >
                  {recipe.share ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-11 w-11 rounded-full ${recipe.isFavorite
                    ? 'bg-blue-500/90 text-white hover:bg-blue-600/90'
                    : 'bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-slate-200'
                    }`}
                  onClick={handleFavoriteToggle}
                  aria-label={recipe.isFavorite ? "Remove from favorites" : "Add to favorites"}
                >
                  <Star
                    className={`h-4 w-4 transition-colors ${recipe.isFavorite
                      ? 'fill-white text-white'
                      : 'text-slate-600 hover:text-yellow-400'
                      }`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-slate-200"
                  onClick={() => ImageGenerationModalService.openModal(recipe, 'detail-page')}
                  disabled={isAssistantLoading}
                  title="Generate AI Image"
                >
                  {isAssistantLoading ? (
                    <div className="animate-spin h-4 w-4 border-2 border-slate-600 border-t-transparent rounded-full" />
                  ) : (
                    <ImagePlus className="h-4 w-4 text-slate-600 hover:text-purple-600" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-slate-200"
                  onClick={handleOpenCleanupPreview}
                  disabled={isCleaningUp || isSubmitting || isAssistantLoading}
                  title="Clean-up Recipe Content"
                >
                  {isCleaningUp ? (
                    <div className="animate-spin h-4 w-4 border-2 border-slate-600 border-t-transparent rounded-full" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-slate-600 hover:text-purple-600" />
                  )}
                </Button>

                {/* Visual separator */}
                <div className="w-px h-6 bg-slate-200"></div>

                {/* Edit & Destructive Actions */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-slate-200"
                  onClick={handleEditClick}
                  title="Edit Recipe"
                >
                  <Edit className="h-4 w-4 text-slate-600 hover:text-blue-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-slate-200"
                  onClick={handleDeleteClick}
                  title="Delete Recipe"
                >
                  <Trash2 className="h-4 w-4 text-slate-600 hover:text-red-600" />
                </Button>
              </div>
            </div>
          </div>

          {/* Recipe Cleanup Preview Modal/Section */}
          {showCleanupPreview && recipe && (
            <div className="my-6">
              <RecipeCleanupPreview
                originalMarkdown={originalMarkdownForCleanup}
                cleanedMarkdown={cleanedMarkdown}
                onAccept={handleAcceptCleanup}
                onReject={handleRejectCleanup}
                isLoading={isCleaningUp && !cleanupError}
                error={cleanupError}
              />
            </div>
          )}

          {/* Recipe Form with proper styling */}
          <div className="max-w-3xl mx-auto">
            <RecipeFormModern
              onSubmit={handleSave}
              initialData={formData}
              isEditMode={true}
              id="edit-recipe-form"
              onCancel={handleCancel}
            />
          </div>
        </div>
      ) : (
        /* View Mode - Show Recipe Content with proper container styling */
        <div className="space-y-4 sm:space-y-6">
          {/* ==================== MOBILE LAYOUT ==================== */}
          <div className="lg:hidden">
            {/* Mobile Header with Image */}
            <div className="relative -mx-4 sm:-mx-6">
              <div className="absolute top-3 left-3 w-auto h-6 z-10">
                <img src="/Cheflab_b.png" alt="Logo Watermark" className="h-full w-auto object-contain opacity-80 pointer-events-none" />
              </div>

              {/* Top-right overlay: Favorites + Share toggle */}
              <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                {(role === 'admin' || recipe.isOwner) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-11 w-11 rounded-full shadow-md ${recipe.share
                      ? 'bg-blue-500/90 text-white hover:bg-blue-600/90'
                      : 'bg-red-500/90 text-white hover:bg-red-600/90'
                      }`}
                    onClick={handleShareToggle}
                    title={recipe.share ? 'Visible to others' : 'Hidden from others'}
                  >
                    {recipe.share ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-11 w-11 rounded-full shadow-md bg-white/80 ${recipe.isFavorite ? 'ring-2 ring-amber-400' : ''}`}
                  onClick={handleFavoriteToggle}
                >
                  <Star className={`h-5 w-5 ${recipe.isFavorite ? 'fill-amber-400 text-amber-500' : 'text-slate-400'}`} />
                </Button>
              </div>

              {recipe.source && (() => {
                // Custom logic for studio recipes to show chef avatar
                if (recipe.source === RecipeSource.Studio) {
                  let chefId: string | undefined;
                  try {
                    const tagArray = typeof recipe.tags === 'string' ? JSON.parse(recipe.tags) : recipe.tags;
                    if (Array.isArray(tagArray)) {
                      chefId = tagArray.find(t => chefPersonas.some(p => p.id === t));
                    }
                  } catch { }

                  const persona = chefPersonas.find(p => p.id === chefId);
                  if (persona) {
                    return (
                      <div className="absolute bottom-3 right-3 z-10 shadow-lg" title={`Created by ${persona.name}`}>
                        <img
                          src={persona.avatar}
                          alt={persona.name}
                          className="h-12 w-12 rounded-full border-2 border-white object-cover bg-white/80"
                        />
                      </div>
                    );
                  }
                }

                const SourceIconComponent = RECIPE_SOURCE_CONFIG[recipe.source]?.icon || DEFAULT_SOURCE_CONFIG.icon;
                const color = RECIPE_SOURCE_CONFIG[recipe.source]?.color || DEFAULT_SOURCE_CONFIG.color;
                return (
                  <div className="absolute bottom-3 right-3 z-10">
                    <SourceIconComponent className={`h-8 w-8 ${color} bg-white/80 rounded-full p-2`} />
                  </div>
                );
              })()}

              <img
                src={recipe.image_url || '/recipe-placeholder.svg'}
                alt={recipe.title}
                className="w-full aspect-[4/3] object-cover"
              />
            </div>

            {/* Mobile Title & Actions */}
            <div className="px-4 pt-4 space-y-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                {recipe.title}
              </h1>

              {/* Metadata Row: Category + Tags inline */}
              <div className="flex items-center flex-wrap gap-2 text-sm">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">
                  {recipe.category}
                </span>
                {recipe.isOwner && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                    Yours
                  </span>
                )}
                {recipe.tags && (() => {
                  const tagArray = typeof recipe.tags === 'string' ? JSON.parse(recipe.tags) : recipe.tags;
                  if (Array.isArray(tagArray)) {
                    const filteredTags = tagArray.filter(t => !chefPersonas.some(p => p.id === t));
                    return filteredTags.map((tag, i) => (
                      <span key={i} className="flex items-center text-slate-700 text-xs font-medium">
                        <span className="w-1 h-1 rounded-full bg-slate-400 mr-1.5" />
                        {tag}
                      </span>
                    ));
                  }
                  return null;
                })()}
              </div>

              {/* Compact Action Toolbar (Icon-First) */}
              <div className="flex items-center gap-1 p-1.5 bg-slate-50 rounded-xl border border-slate-200 w-fit max-w-full overflow-x-auto no-scrollbar">
                <Button variant="ghost" size="icon" onClick={handleCopyRecipe} className="h-11 w-11 rounded-lg hover:bg-white text-slate-700" title="Copy as Markdown">
                  <Clipboard className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={handleCloneRecipe} className="h-11 w-11 rounded-lg hover:bg-white text-slate-700" title="Clone Recipe">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setIsNutritionalAnalysisModalOpen(true)} className="h-11 w-11 rounded-lg hover:bg-white text-slate-700" title="Nutrition Info">
                  <Activity className="h-4 w-4" />
                </Button>

                {(role === 'admin' || recipe.isOwner) && (
                  <>
                    <div className="w-px h-5 bg-slate-200 mx-1" />
                    <Button variant="ghost" size="icon" onClick={() => ImageGenerationModalService.openModal(recipe, 'detail-page')} className="h-11 w-11 rounded-lg hover:bg-white text-slate-700" title="Generate AI Image">
                      <ImagePlus className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleEditClick} className="h-11 w-11 rounded-lg hover:bg-blue-50 text-blue-600" title="Edit Recipe">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleDeleteClick} className="h-11 w-11 rounded-lg hover:bg-red-50 text-red-600" title="Delete Recipe">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Description */}
            {recipe.description && (
              <div className="px-1 pt-4 prose prose-stone max-w-none prose-sm text-slate-800">
                <ReactMarkdown>{recipe.description}</ReactMarkdown>
              </div>
            )}

            {/* Mobile Ingredients */}
            <div className="pt-6">
              <h2 className="text-lg font-bold mb-3 pb-2 border-b border-slate-200 flex items-center gap-2 text-slate-900">
                <span className="p-1.5 rounded-lg bg-orange-100">
                  <ChefHat className="h-4 w-4 text-orange-600" />
                </span>
                Ingredients
              </h2>
              <div className="prose prose-stone max-w-none prose-sm [&_ul]:space-y-1 [&_li]:mb-1 text-slate-800">
                <ReactMarkdown
                  components={{
                    ul: ({ node: _node, ...props }) => <ul {...props} className="list-disc ml-4 space-y-1" />,
                    li: ({ node: _node, ...props }) => <li {...props} className="text-slate-700" />,
                  }}
                >{recipe.ingredients}</ReactMarkdown>
              </div>
            </div>

            {/* Mobile Method */}
            <div className="pt-6">
              <h2 className="text-lg font-bold mb-3 pb-2 border-b border-slate-200 text-slate-900">Method</h2>
              <div className="prose prose-stone max-w-none prose-sm [&_ol]:space-y-4 [&_li]:mb-0 text-slate-800">
                <ReactMarkdown
                  components={{
                    ol: ({ node: _node, ...props }) => <ol {...props} className="list-none ml-0 space-y-4 recipe-steps" />,
                    li: ({ node: _node, children, ...props }) => (
                      <li {...props} className="flex items-start gap-3 text-slate-700 !pl-0">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 text-white text-xs font-bold flex items-center justify-center shadow-sm step-number mt-0.5" />
                        <span className="flex-1 min-w-0">{children}</span>
                      </li>
                    ),
                  }}
                >{recipe.method}</ReactMarkdown>
              </div>
            </div>

            {/* Mobile Notes */}
            {recipe.notes && (
              <div className="pt-6">
                <h2 className="text-lg font-bold mb-3 pb-2 border-b border-slate-200 text-slate-900">Notes</h2>
                <div className="prose prose-stone max-w-none prose-sm bg-amber-50 p-4 rounded-xl border border-amber-200 text-slate-800">
                  <ReactMarkdown>{recipe.notes}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>

          {/* ==================== DESKTOP LAYOUT ==================== */}
          <div className="hidden lg:block">
            {/* Redesigned Hero Section */}
            <div className="flex gap-10 mb-10">
              {/* Left Column: Image with Overlays */}
              <div className="relative flex-shrink-0 w-80">
                <div className="absolute top-4 left-4 w-auto h-6 z-10 transition-opacity group-hover:opacity-100">
                  <img src="/Cheflab_b.png" alt="Logo" className="h-full w-auto object-contain opacity-60 drop-shadow-sm" />
                </div>

                {/* Top-right overlay: Favorites + Share */}
                <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                  {(role === 'admin' || recipe.isOwner) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-10 w-10 rounded-full shadow-lg backdrop-blur-md transition-all ${recipe.share
                        ? 'bg-blue-500/90 text-white hover:bg-blue-600'
                        : 'bg-white/80 text-slate-500 hover:text-slate-700'
                        }`}
                      onClick={handleShareToggle}
                      title={recipe.share ? 'Visible to others' : 'Private recipe'}
                    >
                      {recipe.share ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-10 w-10 rounded-full shadow-lg backdrop-blur-md transition-all bg-white/80 ${recipe.isFavorite ? 'text-amber-500' : 'text-slate-500'
                      }`}
                    onClick={handleFavoriteToggle}
                  >
                    <Star className={`h-4 w-4 ${recipe.isFavorite ? 'fill-amber-500' : ''}`} />
                  </Button>
                </div>

                {/* Chef Badge */}
                {recipe.source === RecipeSource.Studio && (() => {
                  let chefId: string | undefined;
                  try {
                    const tagArray = typeof recipe.tags === 'string' ? JSON.parse(recipe.tags) : recipe.tags;
                    if (Array.isArray(tagArray)) {
                      chefId = tagArray.find(t => chefPersonas.some(p => p.id === t));
                    }
                  } catch { }

                  const persona = chefPersonas.find(p => p.id === chefId);
                  if (persona) {
                    return (
                      <div className="absolute bottom-4 right-4 z-10 shadow-2xl transition-transform hover:scale-110" title={`Created by ${persona.name}`}>
                        <img
                          src={persona.avatar}
                          alt={persona.name}
                          className="h-12 w-12 rounded-full border-2 border-white object-cover bg-white"
                        />
                      </div>
                    );
                  }
                })()}

                <img
                  src={recipe.image_url || '/recipe-placeholder.svg'}
                  alt={recipe.title}
                  className="w-full aspect-square object-cover rounded-3xl shadow-2xl ring-1 ring-black/5"
                />
              </div>

              {/* Right Column: Title, Metadata, Toolbar */}
              <div className="flex-1 flex flex-col justify-center min-w-0 py-2">
                {/* 1. Title */}
                <h1 className="text-5xl font-extrabold tracking-tight text-slate-900 mb-6 leading-tight">
                  {recipe.title}
                </h1>

                {/* 2. Description */}
                {recipe.description && (
                  <div className="prose prose-stone max-w-none text-slate-700 mb-8 text-lg leading-relaxed line-clamp-3">
                    <ReactMarkdown>{recipe.description}</ReactMarkdown>
                  </div>
                )}

                {/* 3. Metadata Row (Clean context) */}
                <div className="flex items-center gap-4 mb-10 overflow-x-auto no-scrollbar">
                  <span className="px-4 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold tracking-wide border border-slate-200">
                    {recipe.category}
                  </span>

                  {/* Filtered Tags (Dietary/Lifestyle only) */}
                  {recipe.tags && (() => {
                    const tagArray = typeof recipe.tags === 'string' ? JSON.parse(recipe.tags) : recipe.tags;
                    if (Array.isArray(tagArray)) {
                      const lifestyleTags = tagArray.filter(t => !chefPersonas.some(p => p.id === t));
                      return lifestyleTags.map((tag, i) => (
                        <span key={i} className="flex items-center text-slate-600 text-sm font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-2" />
                          {tag}
                        </span>
                      ));
                    }
                  })()}

                  {/* Estimated Read Time (Decorative but premium) */}
                  <span className="flex items-center text-slate-500 text-xs font-medium uppercase tracking-widest ml-auto">
                    <Clock className="w-3.5 h-3.5 mr-1.5 opacity-60" />
                    5 MIN READ
                  </span>
                </div>

                {/* 4. Tiered Action Toolbar (The Clean Fix) */}
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-2xl border border-slate-200 w-fit">
                  {/* READER GROUP */}
                  <div className="flex items-center gap-1.5 px-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyRecipe} className="rounded-xl hover:bg-white transition-all text-slate-700 h-10 px-4" title="Copy as Markdown">
                      <Clipboard className="h-4 w-4 mr-2 text-slate-400" />
                      Copy
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleCloneRecipe} className="rounded-xl hover:bg-white transition-all text-slate-700 h-10 px-4" title="Create a personal copy">
                      <Copy className="h-4 w-4 mr-2 text-slate-400" />
                      Clone
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setIsNutritionalAnalysisModalOpen(true)} className="rounded-xl hover:bg-white transition-all text-slate-700 h-10 px-4" title="View nutrition facts">
                      <Activity className="h-4 w-4 mr-2 text-slate-400" />
                      Nutrition
                    </Button>
                  </div>

                  {/* Vertical Divider */}
                  {(role === 'admin' || recipe.isOwner) && (
                    <div className="w-px h-6 bg-slate-200 mx-2" />
                  )}

                  {/* OWNER GROUP */}
                  {(role === 'admin' || recipe.isOwner) && (
                    <div className="flex items-center gap-1.5 px-2">
                      <Button variant="ghost" size="icon" onClick={() => ImageGenerationModalService.openModal(recipe, 'detail-page')} className="rounded-xl hover:bg-white transition-all text-slate-700 h-10 w-10" title="Generate AI Image">
                        <ImagePlus className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={handleEditClick} className="rounded-xl hover:bg-blue-50 transition-all text-blue-600 h-10 w-10" title="Edit Recipe">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={handleDeleteClick} className="rounded-xl hover:bg-red-50 transition-all text-red-600 h-10 w-10" title="Delete Recipe">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content: 2-column layout with sticky ingredients */}
            <div className="flex gap-8">
              {/* Left Column: Sticky Ingredients Card */}
              <div className="w-80 flex-shrink-0">
                <div className="sticky top-4">
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-amber-50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="p-2 rounded-xl bg-orange-100">
                          <ChefHat className="h-5 w-5 text-orange-600" />
                        </span>
                        <h2 className="text-xl font-bold text-slate-900">Ingredients</h2>
                      </div>
                      <div className="prose prose-stone max-w-none prose-sm [&_ul]:space-y-2 [&_li]:mb-0 text-slate-800">
                        <ReactMarkdown
                          components={{
                            ul: ({ node: _node, ...props }) => <ul {...props} className="list-none ml-0 space-y-2" />,
                            li: ({ node: _node, children, ...props }) => (
                              <li {...props} className="flex items-start gap-2 text-slate-700">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                                <span>{children}</span>
                              </li>
                            ),
                          }}
                        >{recipe.ingredients}</ReactMarkdown>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Notes Card - if short, show here */}
                  {recipe.notes && recipe.notes.length < 500 && (
                    <Card className="mt-4 border-0 shadow-lg bg-amber-50 border-l-4 border-l-amber-400">
                      <CardContent className="p-5">
                        <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wide mb-2">Chef's Notes</h3>
                        <div className="prose prose-stone max-w-none prose-sm text-amber-900">
                          <ReactMarkdown>{recipe.notes}</ReactMarkdown>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {/* Right Column: Method */}
              <div className="flex-1 min-w-0">
                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-4 border-b border-slate-200">
                      Method
                    </h2>
                    <div className="prose prose-stone max-w-none prose-lg [&_ol]:space-y-6 [&_li]:mb-0 text-slate-800">
                      <ReactMarkdown
                        components={{
                          ol: ({ node: _node, ...props }) => <ol {...props} className="list-none ml-0 space-y-6 recipe-steps" />,
                            li: ({ node: _node, children, ...props }) => (
                              <li {...props} className="flex items-start gap-4 text-slate-700 !pl-0">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 text-white text-sm font-bold flex items-center justify-center shadow-sm step-number mt-1" />
                                <span className="flex-1 min-w-0">{children}</span>
                              </li>
                            ),
                        }}
                      >{recipe.method}</ReactMarkdown>
                    </div>

                    {/* Long notes go here */}
                    {recipe.notes && recipe.notes.length >= 500 && (
                      <div className="mt-8 pt-6 border-t border-slate-200">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">Chef's Notes</h3>
                        <div className="prose prose-stone max-w-none prose-base bg-amber-50 p-5 rounded-xl border border-amber-200 text-slate-800">
                          <ReactMarkdown>{recipe.notes}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {(role === 'admin' || recipe?.isOwner) && (
        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          modal={false}
        >
          <DialogContent
            className="w-full sm:max-w-md max-h-[100dvh] sm:max-h-[85vh] p-4 sm:p-6"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="relative">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute -top-1 -right-1 z-20 h-10 w-10 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </DialogClose>
              <DialogHeader className="pr-10">
                <DialogTitle>Delete Recipe</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{recipe.title}"? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6">
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    disabled={recipesLoading}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                  disabled={recipesLoading}
                  className="w-full sm:w-auto"
                >
                  {recipesLoading ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Nutritional Analysis Modal */}
      {recipe && (
        <RecipeNutritionalModal
          recipe={{
            id: recipe.id,
            title: recipe.title,
            ingredients: recipe.ingredients,
            method: recipe.method
          }}
          isOpen={isNutritionalAnalysisModalOpen}
          onClose={() => setIsNutritionalAnalysisModalOpen(false)}
        />
      )}
    </div>
  );
};

export default RecipeDetailPage;
